import { useState, useEffect, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  IconMail,
  IconPencil,
  IconPlus,
  IconSearch,
  IconSpinner,
  IconTrash,
} from "../icons";
import UserForm from "../components/UserForm";
import { useAdminModal } from "../components/AdminModalProvider";
import { notifyError, notifySuccess } from "../notify";

export default function UserManagement() {
  const { confirm } = useAdminModal();
  const { setPageSubtitle } = useOutletContext() || {};
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(
    () =>
      users.filter((user) =>
        user.email.toLowerCase().includes(searchTerm.trim().toLowerCase())
      ),
    [users, searchTerm]
  );

  useEffect(() => {
    if (!setPageSubtitle) return undefined;

    const parts = [
      `Showing ${filteredUsers.length} of ${users.length} users`,
    ];
    if (searchTerm.trim()) {
      parts.push(`matching "${searchTerm.trim()}"`);
    }
    setPageSubtitle(parts.join(" · "));

    return () => setPageSubtitle(null);
  }, [filteredUsers.length, users.length, searchTerm, setPageSubtitle]);

  const fetchUsers = async () => {
    try {
      const response = await fetch("/api/users", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      notifyError(error.message);
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, email) => {
    const confirmed = await confirm({
      title: "Delete user?",
      description: `Are you sure you want to delete "${email}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      tone: "danger",
    });

    if (!confirmed) return;

    setActionLoading(true);
    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to delete user");
      }
      setUsers((prev) => prev.filter((user) => user.id !== userId));
      notifySuccess("User deleted successfully");
    } catch (error) {
      notifyError(error.message);
      console.error("Error deleting user:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedUser(null);
    fetchUsers();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <IconSpinner size={32} />
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div
        className="user-management__toolbar"
        role="toolbar"
        aria-label="User filters"
      >
        <label className="user-management__search">
          <IconSearch
            size={16}
            className="user-management__search-icon"
            aria-hidden
          />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by email…"
            aria-label="Search users"
          />
        </label>

        <button
          type="button"
          className="user-management__add"
          onClick={handleAdd}
          disabled={actionLoading}
        >
          <IconPlus size={16} />
          Add New
        </button>
      </div>

      <div className="user-management__table-wrap">
        {filteredUsers.length === 0 ? (
          <div className="user-management__empty">
            {searchTerm.trim()
              ? `No users match "${searchTerm.trim()}".`
              : "No users found. Click Add New to create one."}
          </div>
        ) : (
          <table className="user-management__table">
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col" className="user-management__actions-col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-management__user-cell">
                      <span
                        className="user-management__avatar"
                        aria-hidden="true"
                      >
                        {user.email.charAt(0).toUpperCase()}
                      </span>
                      <div className="user-management__user-info">
                        <span className="user-management__email">
                          {user.email}
                        </span>
                        <span className="user-management__user-meta">
                          <IconMail size={14} aria-hidden />
                          Admin user
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="user-management__actions-col">
                    <div className="user-management__actions">
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={() => handleEdit(user)}
                        disabled={actionLoading}
                        title="Edit user"
                      >
                        <IconPencil size={18} />
                      </button>
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => handleDelete(user.id, user.email)}
                        disabled={actionLoading}
                        title="Delete user"
                      >
                        <IconTrash size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isFormOpen && (
        <UserForm user={selectedUser} onClose={handleFormClose} />
      )}
    </div>
  );
}
